import { Page } from '@web/components/page';
import { withServerProps } from '@web/library/with-server-props';
import {
  IndexBreedersListScreen,
  IndexBreedersListScreenProps,
} from 'shared/screens/breeders/index-breeders-list';
import { useTranslation } from 'react-i18next';
import { PageBuilder } from 'shared/library/page-builder';
import { Animal, Breeder } from 'shared/@generated/models';
import { Paginator } from 'shared/@types';
import {
  BreedersFilter,
  getBreedersApiParams,
  parseBreedersFilter,
} from 'shared/state/breeders-filter';
import { apiRoute } from 'shared/library/api-route';
import { prepareDataForApi } from 'shared/library/api-client';

type BreederCategoryConfig = {
  animal: Animal;
  translationKey: string;
};

type BreederCategoryParams = BreedersFilter & { animal?: Animal };

function getCategoryParams(params: BreederCategoryParams, config: BreederCategoryConfig) {
  return {
    ...params,
    animal: config.animal,
  };
}

export function createBreederCategoryPage(config: BreederCategoryConfig) {
  return function BreederCategoryPage(props: IndexBreedersListScreenProps) {
    const { t } = useTranslation('screens');
    const title = t(`breeders.index_breeders_list.categories.${config.translationKey}.title`);
    const description = t(
      `breeders.index_breeders_list.categories.${config.translationKey}.description`,
    );

    return (
      <Page {...props} register={(seo) => seo.setTitle(title).setDescription(description)}>
        <IndexBreedersListScreen
          {...props}
          breedAnimal={config.animal}
          title={title}
          subtitle={description}
        />
      </Page>
    );
  };
}

export function createBreederCategoryPageBuilder(config: BreederCategoryConfig) {
  return new PageBuilder<IndexBreedersListScreenProps, BreederCategoryParams>().request(
    ({ req, params }) => {
      const categoryParams = getCategoryParams(params, config);

      return req<{ breeders: Paginator<Breeder> }>(apiRoute('breeders.index'), {
        ...prepareDataForApi(getBreedersApiParams(categoryParams)),
        with_details: true,
      }).then((data) => ({
        initialBreeders: data.breeders,
        initialFilter: parseBreedersFilter(params),
      }));
    },
  );
}

export function createBreederCategoryGetServerSideProps(config: BreederCategoryConfig) {
  return withServerProps(createBreederCategoryPageBuilder(config));
}
